import React, { useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { withStyles, Typography, Tooltip, IconButton } from '@material-ui/core';
import MobileEraseStationList from './MobileEraseStationList';
import { hasPermission } from '../../sc-utilities';
import { More, SettingsBackupRestore, SettingsRemote } from '@material-ui/icons';
import { copyToClipBoard } from '../../sc-utilities';
import { snackActions } from '../../sc-utilities/SnackBar';
import * as actions from "./store/mobileEraseStationActions";

const styles = theme => ({
    "@global": {
        "[class*='paletteButtons']": {
            backgroundColor: theme.palette.primary.main,
            color: 'none',
            margin: theme.spacing(),
            '&:hover': {
                backgroundColor: theme.palette.secondary.main,
            },
        },
        "[class*='active']": {
            color: theme.palette.success.dark
        },
        "[class*='inactive']": {
            color: theme.palette.error.dark
        }
    },
    root: {
        width: `100%`,
        height: 'auto',
        paddingLeft: theme.spacing(-1),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%'
        }
    },
    active: {
        color: theme.palette.success.dark
    },
    inactive: {
        color: theme.palette.error.dark
    },
    remote: {
        textDecoration: 'none',
        color: 'white'
    }
});

export const isValid = data => {
    return data !== null && data !== undefined;
};

const MobileEraseStation = props => {
    const tableRef = useRef();
    const { columns, classes, translations, permissions, onRevokeMobileEraseStation, activeCustomerID } = props;
    let visible = columns && columns['MobileEraseStation'];
    const [mobileEraseStationDetailsInfo, setMobileEraseStationDetailsInfo] = useState({ open: false });

    const handleRevoke = async (data) => {
        await onRevokeMobileEraseStation({ HardwareID: data.HardwareID, ActiveCustomerID: activeCustomerID });
        tableRef.current && tableRef.current.onQueryChange();
    }

    const tableColumns = useMemo(() => {
        const handleOpenRemote = async (rowData) => {
            copyToClipBoard(rowData.RemotePassword);
            snackActions.snackBarComponent((<Typography variant={'caption'} ><a className={classes.remote} rel="noopener noreferrer" href="https://anydesk.com/" target="_blank">
                {translations['securaze_remote_snackbar_message'] || "Password is copied to clipboard. NOTE: Make sure you have installed AnyDesk"}
            </a></Typography>), 'info');
            const element = document.createElement('a');
            element.href = `anydesk:${rowData.RemoteID}`;
            element['data-href-alt'] = "https://anydesk.com/"
            return element.click();
        };

        return [
            {
                title: translations["action"] || "Action", field: 'Action',
                hidden: visible ? visible['Action'] : false,
                filtering: false,
                render: rowData => <>
                    {
                        <Tooltip title={translations['open_remote_erasure'] || 'Open Securaze Remote'} >
                            <IconButton
                                disabled={!rowData.RemoteID || !rowData.RemotePassword || !(rowData.Status === 'active')}
                                id={`mobile_erase_station_open_remote_${rowData.MobileEraseStationID}`}
                                component='div'
                                onClick={() => handleOpenRemote(rowData)}
                            >
                                <SettingsRemote />
                            </IconButton>
                        </Tooltip>
                    }
                    {
                        <Tooltip title={translations['mobile_erase_station_details'] || 'Mobile station details'} >
                            <IconButton
                                id={`mobile_erase_station_details_${rowData.MobileEraseStationID}`}
                                component='div'
                                onClick={() => openMobileEraseStationDetailsModal(rowData)}
                            >
                                <More />
                            </IconButton>
                        </Tooltip>
                    }
                    {
                        hasPermission(permissions, 'mobile_erase_station_revoke_license') && <Tooltip title={translations['revoke'] || 'Revoke'} >
                            <IconButton
                                id={`revoke_${rowData.MobileEraseStationID}`}
                                component='div'
                                onClick={() => handleRevoke(rowData)}
                                disabled={!rowData.ApiKey}
                            >
                                <SettingsBackupRestore />
                            </IconButton>
                        </Tooltip>
                    }
                </>
            },
            {
                title: translations["name"] || 'Name', field: 'Name',
                hidden: visible ? visible['Name'] : false,
                filtering: true,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.Name ? rowData.Name : 'n/a'}</Typography>;
                }
            },
            {
                title: translations["hardware_id"] || 'Hardware ID', field: 'HardwareID',
                hidden: visible ? visible['HardwareID'] : false,
                filtering: true,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.HardwareID ? rowData.HardwareID : 'n/a'}</Typography>;
                }
            },
            {
                title: translations["version"] || 'Version', field: 'Version',
                hidden: visible ? visible['Version'] : false,
                filtering: false,
                render: rowData => {
                    return <Typography variant={'caption'}>{isValid(rowData.MajorVersion) && isValid(rowData.MinorVersion) && isValid(rowData.BuildNumber) ? `${rowData.MajorVersion}.${rowData.MinorVersion}.${rowData.BuildNumber}` : 'n/a'}</Typography>;
                }
            },
            {
                title: translations["status"] || 'Status', field: 'Status',
                hidden: visible ? visible['Status'] : false,
                filtering: true,
                lookup: { 'inactive': translations["inactive"] || 'Inactive', 'active': translations["active"] || 'Active' },
                defaultFilter: ['inactive', 'active'],
                render: rowData => {
                    return <CustomChip key={`status_${rowData.MobileEraseStationID}`} className={rowData.License?.Status === 'active' && rowData.Status === 'active' ? 'active' : 'inactive'} value={rowData.License?.Status === 'active' && rowData.Status === 'active' ? translations["active"] || "Active" : translations["inactive"] || "Inactive"} />;
                }
            },
            {
                title: translations["Online"] || 'Online', field: 'Online',
                hidden: visible ? visible['Online'] : false,
                filtering: true,
                lookup: { '1': translations['yes'] || 'Yes', '0': translations['no'] || 'No' },
                defaultFilter: ['1', '0'],
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.SocketID ? translations['yes'] || 'Yes' : translations['no'] || 'No'}</Typography>;
                }
            }
        ]
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classes, translations, visible, permissions]);

    const openMobileEraseStationDetailsModal = async (rowData) => {
        let details = {
            MobileEraseStationID: rowData.MobileEraseStationID,
            Name: rowData.Name,
            HardwareID: rowData.HardwareID,
            RemoteID: rowData.RemoteID || 'n/a',
            RemotePassword: rowData.RemotePassword || 'n/a',
            License: rowData.License || null
        }
        setMobileEraseStationDetailsInfo({ open: true, ...details });
    };

    const CustomChip = props => {
        const { keyValue, value, className } = props;
        return (
            <Typography key={keyValue} className={className}>{value}</Typography>
        );
    };

    return (
        <div className={classes.root}>
            {
                hasPermission(permissions, 'mobile_erase_station_list') ?
                    <MobileEraseStationList
                        tableColumns={tableColumns}
                        mobileEraseStationDetailsInfo={mobileEraseStationDetailsInfo}
                        setMobileEraseStationDetailsInfo={setMobileEraseStationDetailsInfo}
                        tableRef={tableRef}
                    /> : null
            }
        </div>
    );
};

const mapStateToProps = state => {
    return {
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
        permissions: state?.core?.login?.permissions || []
    };
}

const mapDispatchToProps = dispatch => {
    return {
        onRevokeMobileEraseStation: (data) => dispatch(actions.revokeMobileEraseStation(data)),
    }
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(MobileEraseStation));
